<template>
  <div ref="pageContainer"
    class="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <img v-for="(bee, index) in bees" :key="index" :src="beeImage" alt="Bee"
        class="absolute w-12 h-12 transition-none" :style="{
          left: bee.x + 'px',
          top: bee.y + 'px',
          transform: `scaleX(${bee.vx > 0 ? 1 : -1})`,
        }" />
    </div>

    <div ref="contentContainer" class="max-w-md w-full space-y-8 relative z-10">
      <div class="bg-white rounded-lg shadow-lg p-8 border border-creme-300">
        <div class="text-center">
          <div v-if="messageType === 'verify-email'" class="mb-6">
            <svg class="mx-auto h-16 w-16 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div v-else-if="messageType === 'email-verified'" class="mb-6">
            <svg class="mx-auto h-16 w-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div v-else-if="messageType === 'email-changed'" class="mb-6">
            <svg class="mx-auto h-16 w-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h2 class="mt-6 text-3xl font-marketing font-bold text-primary">
            {{ title }}
          </h2>
          <p class="mt-4 text-base text-gray-700 font-sans leading-relaxed">
{{ message }}
          </p>

          <div class="mt-8">
            <a :href="buttonLink"
              class="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-marketing font-medium rounded-md text-white bg-accent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors">
              {{ buttonText }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const pageContainer = ref(null)
const contentContainer = ref(null)
const beeImage = '/logos/Icon Logo/No Background Black Logo Icon Cropped.png'

const bees = ref([
  { x: 100, y: 150, vx: 0.8, vy: 0.6 },
  { x: 300, y: 200, vx: -0.7, vy: 0.5 },
  { x: 500, y: 100, vx: 0.9, vy: -0.8 },
  { x: 700, y: 300, vx: -0.6, vy: -0.7 },
  { x: 900, y: 250, vx: 0.75, vy: 0.65 },
])

let animationFrame = null

const getMessageType = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get('type') || 'verify-email'
}

const messageType = ref(getMessageType())

const title = computed(() => {
  if (messageType.value === 'email-verified') {
    return 'Account Verified!'
  }
  if (messageType.value === 'email-changed') {
    return 'Email Changed Successfully!'
  }
  return 'Check Your Email'
})

const message = computed(() => {
  if (messageType.value === 'email-verified') {
    return 'Your email account has been verified. You can now log in to access all features.'
  }
  if (messageType.value === 'email-changed') {
    return 'Your email address has been successfully changed. You can now log in with your new email address.'
  }
  return 'Check your email to verify before proceeding. We have sent a verification link to your email address. Please click the link in the email to complete your registration.'
})

const buttonText = computed(() => {
  if (messageType.value === 'email-verified' || messageType.value === 'email-changed') {
    return 'Go to Login'
  }
  return 'Go to Login'
})

const buttonLink = computed(() => {
  if (messageType.value === 'email-verified' || messageType.value === 'email-changed') {
    return '/login'
  }
  return '/login'
})

const updateBees = () => {
  if (!pageContainer.value) {
    animationFrame = requestAnimationFrame(updateBees)
    return
  }

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerLeft = containerRect.left
  const containerRight = containerRect.right
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerWidth = containerRight - containerLeft
  const containerHeight = containerBottom - containerTop

  const cardCenterX = containerLeft + containerWidth / 2
  const cardCenterY = containerTop + containerHeight / 2
  const cardLeft = cardCenterX - 224
  const cardRight = cardCenterX + 224
  const cardTop = cardCenterY - 300
  const cardBottom = cardCenterY + 300

  bees.value.forEach(bee => {
    const prevX = bee.x
    const prevY = bee.y
    
    bee.x += bee.vx
    bee.y += bee.vy

    if (bee.x < 0) {
      bee.x = 0
      if (bee.vx < 0) {
        bee.vx = -bee.vx
        bee.x = Math.max(0, bee.x + bee.vx * 2)
      }
    } else if (bee.x > containerWidth - 48) {
      bee.x = containerWidth - 48
      if (bee.vx > 0) {
        bee.vx = -bee.vx
        bee.x = Math.min(containerWidth - 48, bee.x + bee.vx * 2)
      }
    }

    if (bee.y < 0) {
      bee.y = 0
      if (bee.vy < 0) {
        bee.vy = -bee.vy
        bee.y = Math.max(0, bee.y + bee.vy * 2)
      }
    } else if (bee.y > containerHeight - 48) {
      bee.y = containerHeight - 48
      if (bee.vy > 0) {
        bee.vy = -bee.vy
        bee.y = Math.min(containerHeight - 48, bee.y + bee.vy * 2)
      }
    }

    const beeCenterX = containerLeft + bee.x + 24
    const beeCenterY = containerTop + bee.y + 24
    const prevBeeCenterX = containerLeft + prevX + 24
    const prevBeeCenterY = containerTop + prevY + 24
    const wasInsideCard = prevBeeCenterX > cardLeft && prevBeeCenterX < cardRight && prevBeeCenterY > cardTop && prevBeeCenterY < cardBottom

    if (
      beeCenterX > cardLeft &&
      beeCenterX < cardRight &&
      beeCenterY > cardTop &&
      beeCenterY < cardBottom
    ) {
      if (!wasInsideCard) {
        const distanceFromLeft = beeCenterX - cardLeft
        const distanceFromRight = cardRight - beeCenterX
        const distanceFromTop = beeCenterY - cardTop
        const distanceFromBottom = cardBottom - beeCenterY

        const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromTop, distanceFromBottom)

        if (minDistance === distanceFromLeft || minDistance === distanceFromRight) {
          if ((minDistance === distanceFromLeft && bee.vx < 0) || (minDistance === distanceFromRight && bee.vx > 0)) {
            bee.vx = -bee.vx
            bee.x = prevX + bee.vx * 2
          }
        } else {
          if ((minDistance === distanceFromTop && bee.vy < 0) || (minDistance === distanceFromBottom && bee.vy > 0)) {
            bee.vy = -bee.vy
            bee.y = prevY + bee.vy * 2
          }
        }
      }
    }
  })

  animationFrame = requestAnimationFrame(updateBees)
}

const handleResize = () => {
  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value.forEach(bee => {
    bee.x = Math.min(bee.x, containerWidth - 48)
    bee.y = Math.min(bee.y, containerHeight - 48)
  })
}

onMounted(() => {
  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value = [
    { x: containerWidth * 0.1, y: containerHeight * 0.2, vx: 0.8, vy: 0.6 },
    { x: containerWidth * 0.3, y: containerHeight * 0.3, vx: -0.7, vy: 0.5 },
    { x: containerWidth * 0.5, y: containerHeight * 0.15, vx: 0.9, vy: -0.8 },
    { x: containerWidth * 0.7, y: containerHeight * 0.4, vx: -0.6, vy: -0.7 },
    { x: containerWidth * 0.9, y: containerHeight * 0.3, vx: 0.75, vy: 0.65 },
  ]
  
  updateBees()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
  window.removeEventListener('resize', handleResize)
})
</script>

